/**
 * One live-feed session for an open RFIMS site: runs the stream client, maps
 * every status payload through the bridge into useTwin.applyReadings, keeps
 * useLiveFeed (connection, source, lastSeen, stale) current, and records twin
 * events on connect / disconnect / stale (naming the site as the twin does,
 * data/assetName.ts, while its element is open) plus an alert whenever a card
 * turns yellow or red. Started and stopped by TwinLiveRuntime.
 */
import { assetName } from '../data/assetName';
import { useTwin, type TwinAlert } from '../store';
import type { Reading, StatusColour } from '../types';
import { cardLabel, findSite, isLiveSite, mapSiteReadings } from './influxBridge';
import {
  createLiveClient,
  streamUrlFor,
  type LiveClient,
  type LiveClientOptions,
  type LiveClientState,
  type StatusVia,
} from './liveClient';
import { useLiveFeed } from './liveFeedStore';
import { computeStaleness } from './staleness';
import type { LiveStatusPayload } from './types';

export const STALE_CHECK_MS = 15_000;
export const STREAM_PATH = '/api/live/stream';
export const STATUS_PATH = '/api/live/status';

const CARD_TITLES: Record<string, string> = {
  environmental: 'Environmental',
  host_health: 'System Health',
  network: 'Network',
};

export type CardAlert = Omit<TwinAlert, 'id' | 'acknowledged' | 'timeMs'>;

/** Alerts for cards whose roll-up changed to yellow or red; a card seen for the first time counts as previously green. */
export function cardTransitionAlerts(
  prev: Record<string, StatusColour>,
  next: Record<string, StatusColour>,
  readings: Reading[],
  siteLabel: string,
): CardAlert[] {
  const alerts: CardAlert[] = [];
  for (const [sub, status] of Object.entries(next)) {
    const before = prev[sub] ?? 'green';
    if (status === before || status === 'green') continue;
    const offenders = readings
      .filter((r) => r.subsystemId === sub && r.dataId !== 'overall_status' && r.status === status)
      .map((r) => `${r.dataId} ${r.value}`)
      .join(', ');
    alerts.push({
      severity: status === 'red' ? 'critical' : 'warning',
      title: `${CARD_TITLES[sub] ?? sub} ${cardLabel(status)}`,
      message: `${siteLabel}: ${offenders || cardLabel(status)}`,
      subsystemId: sub,
    });
  }
  return alerts;
}

export interface LiveFeedDeps {
  createClient?: (options: LiveClientOptions) => LiveClient;
  /** Passed to the client: called once when the hub reports the session has ended (the client is closed by then). */
  onSessionEnded?: () => void;
  now?: () => number;
  streamUrl?: string;
  statusUrl?: string;
}

export interface LiveFeedSession {
  stop(): void;
  /** The payload path, exposed so tests can drive it without a socket. */
  handleStatus(payload: LiveStatusPayload, at: string, via: StatusVia): void;
}

export function startLiveFeed(elementId: string, deps: LiveFeedDeps = {}): LiveFeedSession {
  if (!isLiveSite(elementId)) throw new Error(`${elementId} is not a live RFIMS site`);
  const now = deps.now ?? (() => Date.now());
  useLiveFeed.getState().reset();
  useLiveFeed.getState().setSite(elementId);

  let lastCards: Record<string, StatusColour> = {};
  let stale = false;
  let hasPayload = false;
  let openedOnce = false;
  let lastState: LiveClientState = 'idle';
  // The site's title while its element is open in the twin (the store may already hold the next asset), else its id.
  const siteName = (): string => {
    const t = useTwin.getState();
    return t.element?.id === elementId ? assetName(t.asset, t.element, elementId).title : elementId;
  };

  const checkStaleness = (t: number): void => {
    if (!hasPayload) return;
    const s = computeStaleness(useLiveFeed.getState().lastSeen, t);
    if (s.stale === stale) return;
    stale = s.stale;
    useLiveFeed.getState().setStale(stale);
    useTwin.getState().pushEvent({
      severity: stale ? 'warning' : 'info',
      title: stale ? 'Live readings stale' : 'Live readings fresh again',
      message: stale
        ? s.ageMs === null
          ? `${siteName()} has no readings in Influx`
          : `Newest reading for ${siteName()} is ${Math.round(s.ageMs / 60_000)} min old`
        : `${siteName()} is reporting again`,
      equipment: elementId,
    });
  };

  const handleStatus = (payload: LiveStatusPayload, at: string, via: StatusVia): void => {
    const t = now();
    const site = payload._source === 'influxdb' ? findSite(payload, elementId) ?? null : null;
    const mapped = site ? mapSiteReadings(site, t) : null;
    useLiveFeed.getState().applyPayload(payload, site, mapped?.lastSeenMs ?? null, t);
    hasPayload = true;
    if (!site || !mapped) {
      if (useTwin.getState().element?.id === elementId) useTwin.getState().applyReadings([]);
      console.warn(`[live] status payload (${via}, ${at}) has no site ${elementId}`);
      checkStaleness(t);
      return;
    }
    const twin = useTwin.getState();
    // The element changed underneath this session; the runtime is about to stop it.
    if (twin.element?.id !== elementId) return;
    twin.applyReadings(mapped.readings);
    for (const alert of cardTransitionAlerts(lastCards, mapped.cards, mapped.readings, site.label))
      twin.pushAlert(alert);
    lastCards = mapped.cards;
    checkStaleness(t);
  };

  const onState = (state: LiveClientState, detail?: string): void => {
    useLiveFeed.getState().setConnection(state);
    const twin = useTwin.getState();
    if (state === 'open') {
      twin.pushEvent({
        severity: 'info',
        title: openedOnce ? 'Live feed reconnected' : 'Live feed connected',
        message: `Streaming RFIMS readings for ${siteName()}`,
        equipment: elementId,
      });
      openedOnce = true;
    } else if (state === 'reconnecting' && lastState === 'open') {
      twin.pushEvent({
        severity: 'warning',
        title: 'Live feed disconnected',
        message: `Reconnecting${detail ? ` (${detail})` : ''}`,
        equipment: elementId,
      });
    } else if (state === 'polling') {
      twin.pushEvent({
        severity: 'warning',
        title: 'Live feed polling',
        message: detail ?? `Stream unavailable, polling ${STATUS_PATH}`,
        equipment: elementId,
      });
    }
    lastState = state;
  };

  const client = (deps.createClient ?? createLiveClient)({
    streamUrl: deps.streamUrl ?? streamUrlFor(STREAM_PATH),
    statusUrl: deps.statusUrl ?? STATUS_PATH,
    onStatus: handleStatus,
    onState,
    onSessionEnded: deps.onSessionEnded,
  });
  client.start();
  const staleTimer = setInterval(() => checkStaleness(now()), STALE_CHECK_MS);

  return {
    stop() {
      clearInterval(staleTimer);
      client.stop();
      useLiveFeed.getState().reset();
    },
    handleStatus,
  };
}
